<template>
  <div class="balance-checker">
    <gc-panel v-if="info === null" :title="trans('Balance checker')">
      <form class="form">
        <fieldset class="fields">
          <gc-text-field
            id="card-id"
            :label="trans('Card number')"
            :validation="validation.cardId"
            v-model="cardId"
            @blur="formValidator.isValid('cardId')"
            @keyup="cardId = clearSpaces($event.target.value)"
          />

          <gc-text-field
            id="verification-code"
            :label="trans('Pincode')"
            :validation="validation.verificationCode"
            v-model="verificationCode"
            @blur="formValidator.isValid('verificationCode')"
            @keyup="verificationCode = clearSpaces($event.target.value)"
          />
        </fieldset>

        <template v-if="getReCaptchaSiteKey() !== null">
          <div class="recaptcha-container">
            <vue-recaptcha
              :loadRecaptchaScript="true"
              :sitekey="getReCaptchaSiteKey()"
              @verify="recaptchaVerified"
              @expired="recaptchaExpired"
              @error="recaptchaExpired"
            />

            <gc-list
              v-if="this.submitted === true && recaptcha.isVerified === false"
              :display-errors="true"
              :items="['Confirm that you are not a robot']"
            />
          </div>
        </template>

        <div class="actions">
          <gc-button
            @click="getCardInfo"
            :loading="loading"
            :label="trans('Check balance')"
          />
          <template v-if="hasSubmitError">
            <div
              class="error"
              v-html="trans('Giftcard not found').replace('\n', '<br>')"
            />
          </template>
        </div>
      </form>
    </gc-panel>

    <gc-info-panel
      v-else-if="info !== null"
      :info="info"
      @reset="resetForOtherCard"
    />
  </div>
</template>

<script>
  import apiService from '@topgeschenken/front-library-legacy/services/ApiService'

  import GcPanel from './GcPanel';
  import GcTextField from './GcTextField';
  import GcButton from './GcButton';
  import GcInfoPanel from './GcInfoPanel';

  import FormValidator from '../utils/FormValidator';
  import GcList from "./GcList";

  const Required = (value) => {
    return value === null || value.length === 0 ? `Required field` : null;
  }

  const MinLength6 = (value) => {
    return value === null || value.length < 6 ? `Minimum is 6 characters` : null;
  }

  const MinLength10 = (value) => {
    return value === null || value.length < 10 ? `Minimum is 10 characters` : null;
  }

  let VueRecaptcha = window.VueRecaptcha;
  if (!VueRecaptcha) {
    VueRecaptcha = require('vue-recaptcha').default;
  }

  export default {
    name: "GcBalanceChecker",

    components: {
      GcList,
      VueRecaptcha,
      GcInfoPanel,
      GcButton,
      GcTextField,
      GcPanel
    },

    data() {
      return {
        info: null,
        submitted: false,
        loading: false,
        hasSubmitError: false,
        formValidator: null,
        cardId: null,
        verificationCode: null,
        recaptcha: {
          isVerified: true
        },
        validation: {
          cardId: {
            rules: {
              required: Required,
              minLength: MinLength10
            }
          },
          verificationCode: {
            rules: {
              required: Required,
              minLength: MinLength6
            }
          },
        }
      }
    },

    methods: {
      empty(val) {
        return val === null || val === ''
      },

      clearSpaces (str) {
        return str.trim().replace(/\s/g, '')
      },

      reset() {
        this.info = null;
        this.hasSubmitError = false;
        this.loading = false;
        this.submitted = false;
        this.validation = {...this.orgValidation};
      },

      resetForOtherCard() {
        this.reset();
        this.recaptchaExpired();

        this.cardId = null;
        this.verificationCode = null;
      },

      amountFormat(amount) {
        amount = '' + amount;
        amount = amount.replace('.', ',');

        if (amount.split(',').length === 1) {
          amount += ',-'
        }

        amount = amount.replace(',00', ',-');

        return amount;
      },

      dateFormat(date = null) {
        const dt = date.split(' ');
        const d = dt[0].split('-');

        return `${d[2]}-${d[1]}-${d[0]}`
      },

      getCardInfo() {
        this.reset();

        this.submitted = true;

        if (this.formValidator.isValid()) {
          this.loading = true;

          apiService.post('/giftcard/info', {
            cardId: this.cardId,
            verificationCode: this.verificationCode
          }, {
            api_url: this.$balanceChecker.credentials.api_url,
            client_grant:  this.$balanceChecker.credentials.client_grant,
            client_id:  this.$balanceChecker.credentials.client_id,
            responseType: 'json',
          })
            .then(response => {
              this.info = response.data;
            })
            .catch(() => {
              this.hasSubmitError = true;
            })
            .finally(() => {
              this.loading = false;
            });
        }
      },

      recaptchaExpired() {
        this.recaptcha.isVerified = false;
      },

      recaptchaVerified() {
        this.recaptcha.isVerified = true;
      },

      getReCaptchaSiteKey() {
        return this.$balanceChecker.config.reCaptchaSiteKey || null;
      }
    },

    created() {
      this.formValidator = new FormValidator(this);

      if (this.getReCaptchaSiteKey()) {
        this.recaptcha.isVerified = false;
      }

      this.orgValidation = {...this.validation};
    }
  }
</script>

<style>
  .balance-checker {
    width: 100%;
    max-width: 500px;
  }

  .balance-checker .fields {
    border: 0;
    padding: 0;
    margin: 0 0 1.25rem;
  }

  .balance-checker .btn {
    border: none;
    font-size: .9375rem;
    padding: .5rem 1rem;
    color: #fff;
    font-weight: normal;
    background: #424242;
    cursor: pointer;
  }

  .balance-checker .form {
    margin: 0;
  }

  .balance-checker .card-info .list {
    font-size: 1rem;
    margin-bottom: 1.25rem;
  }

  .balance-checker .error {
    display: block;
    color: #B00020;
    font-size: .75rem;
    margin-top: 1rem;
    line-height: 1.4;
  }

  .balance-checker .recaptcha-container {
    margin-bottom: 1rem;
  }
</style>
